import { add, type Duration, format, startOfToday } from 'date-fns'
import { CalendarIcon } from 'lucide-react'

import { Button } from '@/frontend/components/ui/button'
import { Calendar } from '@/frontend/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/frontend/components/ui/popover'
import { cn } from '@/frontend/lib/utils'

interface DatePickerProps {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Disable all dates before this date. */
  fromDate?: Date
  /** Preset durations relative to today, rendered as quick-set buttons. */
  presets?: Duration[]
}

/** Builds a human-readable label from a Duration, e.g. `{ days: 7 }` → "7 days". */
function formatPresetLabel(duration: Duration): string {
  // Friendly labels for common single-unit durations
  const totalParts = [duration.years, duration.months, duration.weeks, duration.days].filter(
    Boolean
  ).length
  if (totalParts <= 1) {
    if (duration.days === 1) return 'Tomorrow'
    if (duration.weeks === 1) return '1 week'
    if (duration.months === 1) return '1 month'
    if (duration.years === 1) return '1 year'
  }

  const parts: string[] = []
  if (duration.years) parts.push(`${duration.years} ${duration.years === 1 ? 'year' : 'years'}`)
  if (duration.months)
    parts.push(`${duration.months} ${duration.months === 1 ? 'month' : 'months'}`)
  if (duration.weeks) parts.push(`${duration.weeks} ${duration.weeks === 1 ? 'week' : 'weeks'}`)
  if (duration.days) parts.push(`${duration.days} ${duration.days === 1 ? 'day' : 'days'}`)
  return parts.join(', ') || 'Today'
}

function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  disabled,
  className,
  fromDate,
  presets,
}: DatePickerProps) {
  const trigger = (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            disabled={disabled}
            data-slot="date-picker"
            data-empty={!value}
            className={cn(
              'h-8 w-full justify-start text-left font-normal data-[empty=true]:text-muted-foreground',
              className
            )}
          />
        }
      >
        <CalendarIcon className="mr-2 size-4" />
        {value ? format(value, 'PPP') : placeholder}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          disabled={fromDate ? { before: fromDate } : undefined}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  )

  if (!presets?.length) return trigger

  return (
    <div className="flex items-center gap-1.5">
      <div className="min-w-0 flex-1">{trigger}</div>
      {presets.map((duration) => (
        <Button
          key={formatPresetLabel(duration)}
          variant="outline"
          disabled={disabled}
          className="rounded-full text-muted-foreground"
          onClick={() => onChange?.(add(startOfToday(), duration))}
        >
          {formatPresetLabel(duration)}
        </Button>
      ))}
    </div>
  )
}

export { DatePicker, type DatePickerProps }
